import { apiService } from './apiService';
import { authEndpoints, userEndpoints } from '@/api/apiEndpoint';
import { FileProps } from '@/common/types/file';
import { fileService } from './fileService';

export interface UserProfile {
  id: number;
  loginId: string;
  name: string;
  phone: string;
  email: string;
  emailVerified: boolean;
  profileImage?: FileProps;
}

export interface CompanyInfo {
  id: number;
  companyName: string;
  position: string;
  department: string;
  email: string;
  nickname?: string;
  departments: {
    id: number;
    name: string;
    isLeader: boolean;
  }[];
}

export interface AccountSettingsResponse {
  userProfile: UserProfile;
  companyInfos: CompanyInfo[];
}

export interface UpdateEmailDto {
  email: string;
}

export interface UpdateAccountSettingsDto {
  email?: string;
  profileImageId?: number;
}

export interface ChangePasswordDto {
  newPassword: string;
  userCi: string;
  token: string;
}

export interface MessagePreferenceResponse {
  alimtalkSmsEnabled: boolean;
  optedOutAt: string | null;
}

export interface UpdateMessagePreferenceDto {
  alimtalkSmsEnabled: boolean;
}

class AccountService {
  async getAccountSettings(): Promise<AccountSettingsResponse> {
    try {
      const response = await apiService.get<AccountSettingsResponse>(
        userEndpoints.getAccountSettings()
      );
      return response;
    } catch (error) {
      console.error('계정 설정 조회 실패:', error);
      throw error;
    }
  }

  async updateAccountSettings(updateData: UpdateAccountSettingsDto & { profileImage?: File }): Promise<void> {
    try {
      let profileImageId: number | undefined;
      
      // 프로필 이미지가 있는 경우 먼저 업로드
      if (updateData.profileImage) {
        const uploadedFiles = await fileService.uploadFileImage([updateData.profileImage]);
        
        if (uploadedFiles && uploadedFiles.length > 0) {
          profileImageId = uploadedFiles[0].id;
        } else {
          throw new Error('파일 업로드에 실패했습니다.');
        }
      }

      // 프로필 이미지 ID와 다른 데이터를 함께 전송
      const requestData: UpdateAccountSettingsDto = {
        email: updateData.email,
        profileImageId
      };

      await apiService.put(userEndpoints.updateProfileImage(), requestData);
    } catch (error) {
      console.error('계정 설정 업데이트 실패:', error);
      throw error;
    }
  }

  async deleteProfileImage(): Promise<void> {
    try {
      await apiService.delete(userEndpoints.deleteProfileImage());
    } catch (error) {
      console.error('프로필 이미지 삭제 실패:', error);
      throw error;
    }
  }

  async changePassword(changePasswordDto: ChangePasswordDto): Promise<void> {
    await apiService.post(authEndpoints.changePassword(), changePasswordDto);
  }

  async getMessagePreference(): Promise<MessagePreferenceResponse> {
    return apiService.get<MessagePreferenceResponse>(
      userEndpoints.getMessagePreference()
    );
  }

  async updateMessagePreference(
    dto: UpdateMessagePreferenceDto
  ): Promise<MessagePreferenceResponse> {
    return apiService.put<MessagePreferenceResponse>(
      userEndpoints.updateMessagePreference(),
      dto
    );
  }
}

export const accountService = new AccountService(); 